import React from 'react';
import { useQuery } from 'react-query';
import axios from 'axios';
import { MapPin, Users, Clock, RefreshCw, Loader } from 'lucide-react';

const FriendFinder = () => {
  // Fetch Friend Finder stats - refresh every 5 seconds
  const { data, isLoading, error, refetch, isFetching } = useQuery(
    'friendFinderStats',
    async () => {
      const response = await axios.get('/api/admin/friend-finder');
      return response.data;
    },
    {
      refetchInterval: 5000, // Auto-refresh every 5 seconds
      refetchIntervalInBackground: true
    }
  );

  const formatDuration = (startedAt) => {
    if (!startedAt) return 'Unknown';
    const minutes = Math.floor((Date.now() - startedAt) / 60000);
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  if (error) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <h2>Error Loading Friend Finder Stats</h2>
        <p>{error.response?.data?.error || 'Failed to load data'}</p>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
        <h2 style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <MapPin size={24} />
          Friend Finder
        </h2>
        <button
          onClick={() => refetch()}
          disabled={isFetching}
          style={{
            padding: '10px 20px',
            backgroundColor: '#6c757d',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '14px'
          }}
        >
          {isFetching ? <Loader size={16} className="spin" /> : <RefreshCw size={16} />}
          Refresh
        </button>
      </div>

      {isLoading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Loader size={32} className="spin" />
          <p>Loading stats...</p>
        </div>
      ) : (
        <>
          {/* Stats Cards */}
          <div style={{ display: 'flex', gap: '20px', marginBottom: '30px' }}>
            <div style={{
              flex: 1,
              backgroundColor: '#fff',
              border: '1px solid #e0e0e0',
              borderRadius: '8px',
              padding: '20px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '36px', fontWeight: 'bold', color: '#007bff' }}>
                {data?.totalConnections || 0}
              </div>
              <div style={{ color: '#666', marginTop: '5px' }}>
                Total Connections
              </div>
            </div>
            <div style={{
              flex: 1,
              backgroundColor: '#fff',
              border: '1px solid #e0e0e0',
              borderRadius: '8px',
              padding: '20px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '36px', fontWeight: 'bold', color: '#28a745' }}>
                {data?.activeSharers || 0}
              </div>
              <div style={{ color: '#666', marginTop: '5px' }}>
                Active Sharers
              </div>
            </div>
          </div>

          {/* Active Sharers Table */}
          <div style={{
            backgroundColor: '#fff',
            border: '1px solid #e0e0e0',
            borderRadius: '8px',
            overflow: 'hidden'
          }}>
            <div style={{
              padding: '15px 20px',
              borderBottom: '1px solid #e0e0e0',
              backgroundColor: '#f8f9fa',
              fontWeight: 'bold'
            }}>
              Active Sharers
            </div>
            
            {(!data?.sharers || data.sharers.length === 0) ? (
              <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
                No active sharers at the moment
              </div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f8f9fa' }}>
                    <th style={{ padding: '12px 20px', textAlign: 'left', borderBottom: '1px solid #e0e0e0' }}>UUID</th>
                    <th style={{ padding: '12px 20px', textAlign: 'left', borderBottom: '1px solid #e0e0e0' }}>Name</th>
                    <th style={{ padding: '12px 20px', textAlign: 'center', borderBottom: '1px solid #e0e0e0' }}>Followers</th>
                    <th style={{ padding: '12px 20px', textAlign: 'center', borderBottom: '1px solid #e0e0e0' }}>Duration</th>
                    <th style={{ padding: '12px 20px', textAlign: 'left', borderBottom: '1px solid #e0e0e0' }}>Location</th>
                  </tr>
                </thead>
                <tbody>
                  {data.sharers.map((sharer, index) => (
                    <tr key={sharer.uuid} style={{ backgroundColor: index % 2 === 0 ? '#fff' : '#f8f9fa' }}>
                      <td style={{ padding: '12px 20px', fontFamily: 'monospace', fontSize: '13px' }}>{sharer.uuid}</td>
                      <td style={{ padding: '12px 20px' }}>{sharer.name || 'Anonymous'}</td>
                      <td style={{ padding: '12px 20px', textAlign: 'center' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                          <Users size={14} /> {sharer.followerCount}
                        </span>
                      </td>
                      <td style={{ padding: '12px 20px', textAlign: 'center' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                          <Clock size={14} /> {formatDuration(sharer.startedAt)}
                        </span>
                      </td>
                      <td style={{ padding: '12px 20px', fontSize: '12px', color: '#666' }}>
                        {sharer.location ? `${sharer.location.lat?.toFixed(4)}, ${sharer.location.lng?.toFixed(4)}` : 'No location'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default FriendFinder;

